import axios from "axios";
import apiClient from "../api/client";
import { getCategoryPath } from "../api/endpoints";
import { CATEGORY_LIST, normalizeCategory } from "../constants/agencyCategories";
import { CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_UPLOAD_URL } from "../api/config";

export const extractProducts = (responseData) =>
  responseData?.data?.products ||
  responseData?.data ||
  responseData ||
  [];

export const fetchProductsByCategory = async (category) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized);
  if (!path) throw new Error(`Unknown category: ${category}`);
  const response = await apiClient.get(path);
  return extractProducts(response.data);
};

export const fetchProductByCategoryAndId = async (category, id) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized, id);
  const response = await apiClient.get(path);
  return response.data?.data || response.data;
};

export const createProduct = async (category, productData) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized);
  const response = await apiClient.post(path, productData);
  return response.data;
};

export const updateProduct = async (category, id, productData) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized, id);
  const response = await apiClient.put(path, productData);
  return response.data;
};

export const deleteProduct = async (category, id) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized, id);
  const response = await apiClient.delete(path);
  return response.data;
};

/** Pending moderation: backend uses activityStatus=inactive */
export const PENDING_ACTIVITY_STATUS = "inactive";

export const fetchPendingProductsByCategory = async (category) => {
  const normalized = normalizeCategory(category);
  const path = getCategoryPath(normalized);
  const response = await apiClient.get(path, {
    params: { activityStatus: PENDING_ACTIVITY_STATUS },
  });
  const products = extractProducts(response.data);
  return (Array.isArray(products) ? products : []).map(formatPendingProduct);
};

export const fetchAllPendingProducts = async () => {
  const batches = await Promise.all(
    CATEGORY_LIST.map((cat) => fetchPendingProductsByCategory(cat))
  );
  return batches.flat();
};

export const formatPendingProduct = (product) => ({
  ...product,
  id: product._id || product.id,
  vendor: product.vendorName,
  vendorContact: product.user?.email || product.vendorEmail || "",
  category: normalizeCategory(product.Category || product.category),
  dateAdded: product.createdAt
    ? new Date(product.createdAt).toLocaleDateString()
    : "",
});

export const approveProduct = async (category, id) =>
  updateProduct(category, id, { activityStatus: "active" });

/** No dedicated reject endpoint — removal via standard DELETE */
export const rejectProduct = async (category, id) =>
  deleteProduct(category, id);

export const findProductAcrossCategories = async (productId) => {
  for (const category of CATEGORY_LIST) {
    try {
      const product = await fetchProductByCategoryAndId(category, productId);
      if (product) {
        return {
          product: {
            ...product,
            id: product._id || product.id,
            Category: product.Category || category,
          },
          category: normalizeCategory(product.Category || category),
        };
      }
    } catch {
      // try next category
    }
  }
  return null;
};

export const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  const response = await axios.post(CLOUDINARY_UPLOAD_URL, formData);
  return response.data.secure_url;
};
